import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";

export interface FaqItem {
  id?: number;
  title: string;
  type: string;
  isActive: boolean;
}

export interface FaqContentItem {
  title: string;
  description: string;
}

export interface FaqTranslation {
  geo: string;
  locale: string;
  items: FaqContentItem[];
}

interface FaqState {
  list: FaqItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetching: boolean;
  isItemFetched: boolean;
  payload?: FaqItem;
  initialPayload?: FaqItem;
  processingItemId?: number;
}

interface GetFaqListPayload {
  limit: number;
  offset: number;
  title?: string;
  types?: string[];
  isActive?: number;
}

const defPayload: FaqItem = {
  title: "",
  type: "",
  isActive: true,
};

const initialState: FaqState = {
  list: [],
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  isItemFetching: false,
  isListFetched: false,
  isItemFetched: false,
  payload: defPayload,
};

export const getFaqItem = createAsyncThunk("faq/getOne", async (id: string) => {
  const res = await instance.get(`${apiRoutes.faqGet}/${id}`);
  return res.data;
});

export const getFaqList = createAsyncThunk(
  "faq/list",
  async (data: GetFaqListPayload) => {
    try {
      const result = await instance.post(apiRoutes.faqList, data);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const createFaq = createAsyncThunk<
  number,
  FaqItem,
  { rejectValue: ApiErrorPayload }
>("faq/create", async (data: FaqItem, { rejectWithValue }) => {
  try {
    const response = await instance.post(apiRoutes.faqCreate, {
      title: data.title,
      type: data.type,
      isActive: data.isActive ? 1 : 0,
    });

    return response.data.id;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateFaq = createAsyncThunk<
  boolean,
  FaqItem,
  { rejectValue: ApiErrorPayload }
>("faq/update", async (data: FaqItem, { rejectWithValue, getState }) => {
  try {
    const state = getState() as FaqState | { cmsFaq: FaqState };
    const initialPayload =
      "cmsFaq" in state ? state.cmsFaq.initialPayload : state.initialPayload;
    const changes = getChangedFields(
      initialPayload,
      data,
      ["title", "type", "isActive"],
      { title: normalizeOptionalText, isActive: (value) => Boolean(value) },
    );
    await instance.put(`${apiRoutes.faqUpdate}/${data.id}`, {
      ...changes,
    });

    return true;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateFaqTranslation = createAsyncThunk<
  boolean,
  { id: number; data: FaqTranslation },
  { rejectValue: ApiErrorPayload }
>("faq/updateTranslation", async ({ id, data }, { rejectWithValue }) => {
  try {
    await instance.put(`${apiRoutes.faqTranslation}/${id}`, {
      geo: data.geo,
      locale: data.locale,
      items: data.items.map((item) => ({
        title: item.title,
        description: item.description,
      })),
    });
    return true;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const deleteFaq = createAsyncThunk(
  "faq/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.faqDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const cmsFaqSlice = createSlice({
  name: "cmsFaq",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },
    handleUpdateFields: (
      state: FaqState,
      action: PayloadAction<Partial<FaqItem>>,
    ) => {
      state.payload = { ...state.payload, ...action.payload } as FaqItem;
    },
    handleClearFields: (state: FaqState) => {
      state.payload = defPayload;
      state.initialPayload = undefined;
      state.isItemFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getFaqList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = (action.payload?.items ?? []).map((item: FaqItem) => ({
        id: item.id,
        title: item.title,
        type: item.type,
        isActive: Boolean(item.isActive),
      }));
      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getFaqItem.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(getFaqItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.isItemFetched = true;
      state.payload = {
        id: action.payload?.id,
        title: action.payload?.title ?? "",
        type: action.payload?.type ?? "",
        isActive: Boolean(action.payload?.isActive),
      };
      state.initialPayload = { ...state.payload };
    });
    builder.addCase(getFaqItem.rejected, (state) => {
      state.isItemFetching = false;
      state.isItemFetched = true;
    });
    builder.addCase(createFaq.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createFaq.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createFaq.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateFaq.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateFaq.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.meta.arg)
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
      state.initialPayload = state.payload ? { ...state.payload } : undefined;
    });
    builder.addCase(updateFaq.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateFaqTranslation.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(updateFaqTranslation.fulfilled, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateFaqTranslation.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(deleteFaq.pending, (state, action) => {
      state.processingItemId = +action.meta.arg;
    });
    builder.addCase(deleteFaq.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteFaq.rejected, (state) => {
      state.processingItemId = undefined;
    });
  },
});

export const { handlePaginate, handleUpdateFields, handleClearFields } =
  cmsFaqSlice.actions;
